import React from 'react';
import { C } from '../constants';
import {
  datosDeAnclaje, tokenizarTarjetaWompi, anclarMedioPago, baseWompiDe,
  listarMediosAnclados, retirarMedioAnclado,
} from '../services/pagos';

/**
 * MEDIO DE PAGO ANCLADO — el titular autoriza una vez y después se le cobra sin estar.
 *
 * 🔐 EL NÚMERO DE LA TARJETA NO PASA POR SOLAR HUB NI POR EL HUB-API. Se tokeniza en el
 * navegador contra Wompi con la llave PÚBLICA y al backend solo viaja el `tok_…` de un solo
 * uso. Si alguien "simplifica" mandando el PAN al servidor, nos mete en alcance PCI-DSS
 * completo — por eso el campo del número nunca se guarda en estado que salga de acá.
 *
 * ⚖️ Y anclar NO es guardar una tarjeta por comodidad: es autorizar un débito futuro. Wompi
 * exige los dos consentimientos (términos + tratamiento de datos) y acá se le MUESTRAN los
 * enlaces reales antes de aceptar — no se marcan por él en silencio. Sin las dos casillas el
 * botón no habilita.
 */

const CAMPO = {
  width: '100%', background: 'transparent', color: 'inherit',
  border: `1px solid ${C.border}`, borderRadius: 6, padding: '8px 10px', fontSize: 13,
};

function soloDigitos(s) {
  return String(s || '').replace(/\D+/g, '');
}

export default function MedioAnclado({ email, duenioTipo = 'cliente', onAnclado }) {
  const [estado, setEstado] = React.useState(null);   // { disponible, aceptacion, motivo }
  const [medios, setMedios] = React.useState([]);
  const [abierto, setAbierto] = React.useState(false);
  const [tyc, setTyc] = React.useState(false);
  const [datos, setDatos] = React.useState(false);
  const [form, setForm] = React.useState({ numero: '', cvc: '', mes: '', anio: '', titular: '' });
  const [msg, setMsg] = React.useState('');
  const [guardando, setGuardando] = React.useState(false);
  const [completo, setCompleto] = React.useState(true);

  React.useEffect(() => {
    let vivo = true;
    datosDeAnclaje()
      .then((r) => { if (vivo) setEstado(r || null); })
      .catch((e) => { if (vivo) setEstado({ disponible: false, motivo: e.message }); });
    return () => { vivo = false; };
  }, []);

  const cargarMedios = React.useCallback(() => {
    if (!email) return;
    listarMediosAnclados(email, duenioTipo)
      .then((r) => {
        setMedios(r?.medios || []);
        // Sin sesión de la suite el hub-api devuelve la tarjeta ENMASCARADA (marca, últimos 4,
        // estado y fecha) y `completo:false`. Alcanza para reconocerla; no para operarla.
        setCompleto(r?.completo !== false);
      })
      .catch(() => { setMedios([]); setCompleto(true); });
  }, [email, duenioTipo]);

  React.useEffect(() => { cargarMedios(); }, [cargarMedios]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const anclar = async () => {
    if (!email) { setMsg('Necesitamos tu correo para anclar el medio.'); return; }
    setGuardando(true);
    setMsg('Autorizando con Wompi…');
    try {
      const pub = estado?.public_key;
      if (!pub) { setMsg('El hub-api no entregó la llave pública de la pasarela.'); return; }
      const tok = await tokenizarTarjetaWompi(
        {
          numero: soloDigitos(form.numero),
          cvc: soloDigitos(form.cvc),
          mes: soloDigitos(form.mes).padStart(2, '0'),
          anio: soloDigitos(form.anio).slice(-2),
          titular: form.titular,
        },
        pub,
        // La base tiene que ser del MISMO ambiente que la llave. Si el servidor no la mandó, se
        // deriva de la llave en vez de caer al default de sandbox: tokenizar una tarjeta real
        // contra la API de pruebas la hace rebotar con «no aceptó la tarjeta», y el titular se
        // va creyendo que su tarjeta no sirve.
        estado?.base || baseWompiDe(pub),
      );
      if (!tok.ok) { setMsg(tok.error); return; }
      const r = await anclarMedioPago({
        email,
        token: tok.token,
        duenio_tipo: duenioTipo,
        acceptance_token: estado?.aceptacion?.acceptance_token,
        accept_personal_auth: estado?.aceptacion?.datos_token,
      });
      if (!r?.ok) { setMsg(r?.error || 'Wompi no aceptó el medio.'); return; }
      // El número nunca se guardó en ningún lado; el formulario se limpia igual.
      setForm({ numero: '', cvc: '', mes: '', anio: '', titular: '' });
      setAbierto(false);
      setMsg('');
      cargarMedios();
      if (onAnclado) onAnclado(r.medio);
      if (r.persistido === false) {
        setMsg('Medio anclado, pero el hub-api no tiene base: no quedará guardado para el próximo cobro.');
      }
    } catch (e) {
      setMsg(e.message || 'No se pudo anclar el medio.');
    } finally {
      setGuardando(false);
    }
  };

  const retirar = async (fuenteId) => {
    setMsg('Retirando…');
    // 🔴 Antes: `.catch(() => {})` y a recargar. Retirar EXIGE sesión de la suite (el hub-api
    // responde 401 con su motivo), así que para un comprador sin sesión el botón no hacía nada
    // y la tarjeta reaparecía sin una palabra. Un fallo que se ve igual que el camino feliz.
    const r = await retirarMedioAnclado({ email, fuente_id: fuenteId, tipo: duenioTipo })
      .catch((e) => ({ ok: false, error: e.message }));
    setMsg(r?.ok ? '' : (r?.error || 'No se pudo retirar el medio.'));
    cargarMedios();
  };

  // Sin llave privada en el hub-api no se puede debitar después: se dice, no se ofrece un
  // botón que iba a fallar.
  if (estado && estado.disponible === false) {
    return (
      <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.45, marginTop: 8 }}>
        Guardar el medio para próximos pagos todavía no está habilitado. {estado.motivo}
      </div>
    );
  }

  const listo = tyc && datos && form.numero && form.cvc && form.mes && form.anio && !guardando;

  return (
    <div style={{ marginTop: 10, borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase', color: C.muted }}>
          Medios guardados
        </span>
        <button type="button" onClick={() => setAbierto((v) => !v)}
          style={{ background: 'transparent', color: C.teal, border: `1px solid ${C.teal}`, borderRadius: 6, padding: '4px 10px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>
          {abierto ? 'Cancelar' : 'Guardar una tarjeta'}
        </button>
      </div>

      {medios.length > 0 && (
        <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
          {/* 🪤 La llave ya no puede ser `fuente_id`: sin sesión no viene (llega enmascarado). */}
          {medios.map((m, i) => (
            <div key={m.fuente_id || `${m.marca || 'x'}-${m.ultimos4 || i}`}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: C.text }}>
              <span>{(m.marca || 'Tarjeta')} •••• {m.ultimos4 || '????'}</span>
              {completo ? (
                <button type="button" onClick={() => retirar(m.fuente_id)}
                  style={{ background: 'transparent', color: C.muted, border: 'none', fontSize: 11, cursor: 'pointer', textDecoration: 'underline' }}>
                  Retirar
                </button>
              ) : (
                // Sin sesión el retiro responde 401. Se dice, en vez de ofrecer un botón que no
                // hace nada: escribir sobre el medio de pago de alguien pide identificarse.
                <span style={{ fontSize: 10.5, color: C.muted }}>Entra con tu sesión para retirarla</span>
              )}
            </div>
          ))}
        </div>
      )}

      {abierto && (
        <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
          <input value={form.titular} onChange={set('titular')} placeholder="Nombre en la tarjeta" style={CAMPO} />
          <input value={form.numero} onChange={set('numero')} inputMode="numeric" autoComplete="cc-number"
            placeholder="Número de la tarjeta" style={CAMPO} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            <input value={form.mes} onChange={set('mes')} inputMode="numeric" placeholder="MM" style={CAMPO} />
            <input value={form.anio} onChange={set('anio')} inputMode="numeric" placeholder="AA" style={CAMPO} />
            <input value={form.cvc} onChange={set('cvc')} inputMode="numeric" autoComplete="cc-csc" placeholder="CVC" style={CAMPO} />
          </div>

          {/* Los dos consentimientos, con el enlace REAL que manda Wompi. Anclar autoriza un
              débito futuro: aceptarlo por el titular sería justo lo que separa un cobro
              automático de un cargo no autorizado. */}
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 11.5, color: C.text, lineHeight: 1.4 }}>
            <input type="checkbox" checked={tyc} onChange={(e) => setTyc(e.target.checked)} />
            <span>
              Acepto los{' '}
              <a href={estado?.aceptacion?.permalink || '#'} target="_blank" rel="noreferrer" style={{ color: C.teal }}>
                términos y condiciones
              </a>{' '}del pago.
            </span>
          </label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 11.5, color: C.text, lineHeight: 1.4 }}>
            <input type="checkbox" checked={datos} onChange={(e) => setDatos(e.target.checked)} />
            <span>
              Autorizo el{' '}
              <a href={estado?.aceptacion?.datos_permalink || '#'} target="_blank" rel="noreferrer" style={{ color: C.teal }}>
                tratamiento de mis datos personales
              </a>{' '}para cobrar a esta tarjeta cuando corresponda.
            </span>
          </label>

          <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.45 }}>
            {/* Decirlo importa: el usuario tiene derecho a saber dónde termina su tarjeta. */}
            Los datos de la tarjeta viajan directo a la pasarela; Solar Hub solo guarda la marca
            y los últimos 4 dígitos. Puedes retirarla cuando quieras.
          </div>

          <button type="button" onClick={anclar} disabled={!listo}
            style={{
              background: listo ? C.teal : C.card2, color: listo ? C.heading : C.muted,
              border: 0, padding: 12, fontWeight: 700, fontSize: 12, borderRadius: 8,
              cursor: listo ? 'pointer' : 'default',
            }}>
            {guardando ? 'Autorizando…' : 'Guardar y autorizar'}
          </button>
        </div>
      )}

      {msg && <div style={{ fontSize: 11.5, color: C.orange, marginTop: 8 }}>{msg}</div>}
    </div>
  );
}
